import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  PageHeader,
  Paper,
  Text,
  Group,
  Button,
  Divider,
  Badge,
  Avatar,
  Title,
  BrandLogo,
  ImagePicker,
  Alert,
  Loader,
} from '@vipso/shared';
import { logout, patchOrg } from '../store/authSlice';
import type { RootState } from '../store';
import { uploadFile } from '../api/uploadFile';
import {
  useMyOrgBillingQuery,
  useMyOrgInvoicesQuery,
  useUpdateOrgLogoMutation,
} from '../api/tenantApi';
import { resolveDisplayLogoUrl } from '../utils/resolveDisplayLogoUrl';
import { getStoredLanguage, setStoredLanguage, type AppLanguage } from '../i18n';
import { useState } from 'react';

function SettingsSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <Paper
      p="lg"
      radius="md"
      style={{
        border: '1px solid var(--mantine-color-gray-3)',
        background: '#fff',
      }}
    >
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={4} c="navy.9">
            {title}
          </Title>
          {description ? (
            <Text size="sm" c="dimmed">
              {description}
            </Text>
          ) : null}
        </Stack>
        {children}
      </Stack>
    </Paper>
  );
}

function MetaRow({ label, value }: { label: string; value?: string | null }) {
  const { t } = useTranslation();
  return (
    <Group justify="space-between" wrap="nowrap" gap="md">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Text size="sm" fw={600} ta="right" style={{ wordBreak: 'break-word' }}>
        {value || t('common.emptyValue')}
      </Text>
    </Group>
  );
}

function formatInr(n: number) {
  return `₹${Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(v?: string | null) {
  if (!v) return '—';
  return new Date(v).toLocaleDateString();
}

function BillingSection() {
  const { t } = useTranslation();
  const { data: billingData, isLoading, isError } = useMyOrgBillingQuery();
  const { data: invoicesData } = useMyOrgInvoicesQuery();
  const billing = billingData?.myOrgBilling;
  const invoices = invoicesData?.myOrgInvoices ?? [];

  if (isLoading) return <Loader size="sm" />;
  if (isError || !billing) {
    return (
      <Text size="sm" c="dimmed">
        {t('settings.billingUnavailable')}
      </Text>
    );
  }

  const daysLeft =
    billing.validUntil != null
      ? Math.ceil((new Date(billing.validUntil).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
      : null;
  const expiringSoon = daysLeft != null && daysLeft <= 30 && daysLeft >= 0;

  const baseYearly = 30000;
  const baseGst = Math.round(baseYearly * 0.18);
  const baseGrand = baseYearly + baseGst;

  const optionalExtras = (billing.addOnCatalogue ?? []).filter(
    (a) => a.isActive && a.key !== 'org.seats',
  );
  const extraSeat = (billing.addOnCatalogue ?? []).find((a) => a.key === 'org.seats');

  return (
    <Stack gap="md">
      {billing.operationsLocked ? (
        <Alert color="red" title={t('settings.operationsLocked')}>
          {t('settings.operationsLockedHint')}
        </Alert>
      ) : null}
      {expiringSoon ? (
        <Alert color="orange" title={t('settings.planExpiring')}>
          {t('settings.planExpiringHint', { days: daysLeft })}
        </Alert>
      ) : null}

      <Stack gap="sm">
        <MetaRow label={t('settings.plan')} value={billing.planName} />
        <MetaRow label={t('common.status')} value={billing.status} />
        <MetaRow label={t('settings.validFrom')} value={formatDate(billing.validFrom)} />
        <MetaRow label={t('settings.validUntil')} value={formatDate(billing.validUntil)} />
        <MetaRow
          label={t('settings.seats')}
          value={`${billing.seatsUsed} / ${billing.seatsAllowed}`}
        />
      </Stack>

      <Divider label={t('settings.planBreakup')} labelPosition="left" />
      <Stack gap={6}>
        <MetaRow
          label={t('settings.basePlanLine')}
          value={`${formatInr(baseYearly)} / ${t('settings.perYear')}`}
        />
        <MetaRow
          label={t('settings.gstLine', { pct: 18 })}
          value={formatInr(baseGst)}
        />
        <MetaRow
          label={t('settings.grandTotalLine')}
          value={`${formatInr(baseGrand)} / ${t('settings.perYear')}`}
        />
        <Text size="xs" c="dimmed">
          {t('settings.basePlanIncludes')}
        </Text>
      </Stack>

      {billing.seatsUsed >= billing.seatsAllowed ? (
        <Text size="sm" c="dimmed">
          {t('settings.needMoreSeats', {
            price: formatInr(billing.extraSeatYearly),
          })}
        </Text>
      ) : null}

      {billing.activeAddOns.length ? (
        <>
          <Divider label={t('settings.activeAddOns')} labelPosition="left" />
          <Group gap={6}>
            {billing.activeAddOns.map((k) => (
              <Badge key={k} variant="light" color="brand">
                {k === 'entity.approvals' ? t('settings.entityApprovalsName') : k}
              </Badge>
            ))}
          </Group>
        </>
      ) : null}

      <Divider label={t('settings.optionalExtras')} labelPosition="left" />
      <Text size="xs" c="dimmed">
        {t('settings.optionalExtrasHint')}
      </Text>
      <Stack gap="xs">
        {extraSeat ? (
          <Group justify="space-between" wrap="nowrap">
            <Stack gap={0}>
              <Text size="sm" fw={600}>
                {t('settings.extraSeatName')}
              </Text>
              <Text size="xs" c="dimmed">
                {t('settings.extraSeatDesc')}
              </Text>
            </Stack>
            <Text size="sm" fw={600}>
              {formatInr(billing.extraSeatYearly)}/{t('settings.perSeatYear')}
            </Text>
          </Group>
        ) : null}
        {optionalExtras.map((addon) => (
          <Group key={addon.key} justify="space-between" wrap="nowrap">
            <Stack gap={0}>
              <Text size="sm" fw={600}>
                {addon.key === 'entity.approvals'
                  ? t('settings.entityApprovalsName')
                  : addon.name}
                {addon.isSubscribed ? (
                  <Badge size="xs" color="green" style={{ marginLeft: 6 }}>
                    {t('common.active')}
                  </Badge>
                ) : null}
              </Text>
              <Text size="xs" c="dimmed">
                {addon.key === 'entity.approvals'
                  ? t('settings.entityApprovalsDesc')
                  : addon.description}
              </Text>
            </Stack>
            <Text size="sm" fw={600}>
              {formatInr(addon.yearlyPrice)}/{t('settings.perYear')}
            </Text>
          </Group>
        ))}
      </Stack>

      <Divider label={t('settings.invoices')} labelPosition="left" />
      {invoices.length === 0 ? (
        <Text size="sm" c="dimmed">
          {t('settings.noInvoices')}
        </Text>
      ) : (
        <Stack gap="xs">
          {invoices.map((inv) => {
            const subtotal = Number(inv.total ?? inv.subtotal ?? 0);
            const tax = Number(inv.taxTotal ?? 0);
            const grand = Number(inv.grandTotal ?? subtotal + tax);
            return (
              <Stack key={inv.id} gap={4}>
                <Group justify="space-between" wrap="wrap">
                  <Stack gap={0}>
                    <Text size="sm" fw={600}>
                      {inv.invoiceNumber}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {formatDate(inv.issuedAt)} · {inv.status}
                    </Text>
                  </Stack>
                  <Group gap="sm">
                    <Text size="sm" fw={600}>
                      {formatInr(grand)}
                    </Text>
                    {inv.pdfFileId ? (
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => {
                          window.open(
                            `/api/files/${inv.pdfFileId}`,
                            '_blank',
                            'noopener,noreferrer',
                          );
                        }}
                      >
                        {t('settings.downloadPdf')}
                      </Button>
                    ) : null}
                  </Group>
                </Group>
                <Text size="xs" c="dimmed">
                  {t('settings.invoiceBreakup', {
                    subtotal: formatInr(subtotal),
                    gst: formatInr(tax),
                    total: formatInr(grand),
                  })}
                </Text>
              </Stack>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}

export function SettingsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [language, setLanguage] = useState<AppLanguage>(() => getStoredLanguage());
  const { user, org, theme, config, featureKeys, accessToken } = useSelector(
    (state: RootState) => state.auth,
  );
  const [updateOrgLogo] = useUpdateOrgLogoMutation();
  const displayLogoUrl = resolveDisplayLogoUrl(org, config, theme);
  const canViewBilling = featureKeys.includes('billing.view');

  function handleLogout() {
    dispatch(logout());
    navigate('/boot', { replace: true });
  }

  function handleLanguageChange(lang: AppLanguage) {
    setLanguage(lang);
    setStoredLanguage(lang);
  }

  return (
    <Stack gap="lg">
      <PageHeader title={t('settings.title')} description={t('settings.description')} />

      <Paper
        p="lg"
        radius="md"
        style={{
          border: '1px solid var(--mantine-color-gray-3)',
          background: 'linear-gradient(135deg, var(--mantine-color-brand-0) 0%, #fff 55%)',
        }}
      >
        <Group justify="space-between" align="center" wrap="wrap">
          <Group gap="md">
            <BrandLogo logoUrl={displayLogoUrl} size={44} withWordmark={false} />
            <Stack gap={2}>
              <Text fw={700} size="lg" c="navy.9">
                {org?.name || t('settings.orgFallback')}
              </Text>
              <Text size="sm" c="dimmed">
                {user?.name || t('settings.userFallback')}
              </Text>
            </Stack>
          </Group>
          <Badge color="brand" variant="light" size="lg">
            {org?.status || t('common.active')}
          </Badge>
        </Group>
      </Paper>

      <BoxGrid>
        <SettingsSection title={t('settings.orgSection')} description={t('settings.orgSectionDesc')}>
          <ImagePicker
            value={org?.logoUrl || null}
            label={t('settings.orgLogo')}
            shape="square"
            size={72}
            onUpload={async (file) => {
              if (!accessToken) throw new Error(t('auth.notSignedIn'));
              const uploaded = await uploadFile(file, accessToken);
              const result = await updateOrgLogo({ fileId: uploaded.id }).unwrap();
              const logoUrl = result.updateOrgLogo?.logoUrl ?? uploaded.url;
              dispatch(patchOrg({ logoUrl, logoFileId: uploaded.id }));
            }}
            onClear={async () => {
              await updateOrgLogo({ fileId: null }).unwrap();
              dispatch(patchOrg({ logoUrl: null, logoFileId: null }));
            }}
          />
          <Stack gap="sm">
            <MetaRow label={t('common.name')} value={org?.name} />
            <MetaRow label={t('common.status')} value={org?.status} />
            <MetaRow label={t('common.phone')} value={org?.phone} />
            <MetaRow label={t('settings.uniqueId')} value={org?.uniqueId} />
          </Stack>
        </SettingsSection>

        <SettingsSection
          title={t('settings.accountSection')}
          description={t('settings.accountSectionDesc')}
        >
          <Group gap="md" mb="sm">
            <Avatar color="brand" radius="xl" size={48}>
              {(user?.name || 'U').slice(0, 1).toUpperCase()}
            </Avatar>
            <Stack gap={0}>
              <Text fw={600}>{user?.name || t('common.emptyValue')}</Text>
              <Text size="sm" c="dimmed">
                {user?.phone || user?.email || t('common.emptyValue')}
              </Text>
            </Stack>
          </Group>
          <Stack gap="sm">
            <MetaRow label={t('common.phone')} value={user?.phone} />
            <MetaRow label={t('common.email')} value={user?.email} />
          </Stack>
        </SettingsSection>

        <SettingsSection title={t('settings.language')} description={t('settings.languageHint')}>
          <Group gap="xs">
            {(
              [
                { value: 'en', label: t('settings.english') },
                { value: 'gu', label: t('settings.gujarati') },
                { value: 'hi', label: t('settings.hindi') },
              ] as const
            ).map((opt) => (
              <Button
                key={opt.value}
                size="sm"
                variant={language === opt.value ? 'filled' : 'default'}
                onClick={() => handleLanguageChange(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </Group>
        </SettingsSection>

        {canViewBilling ? (
          <SettingsSection
            title={t('settings.billingSection')}
            description={t('settings.billingSectionDesc')}
          >
            <BillingSection />
          </SettingsSection>
        ) : null}
      </BoxGrid>

      <Divider />

      <Group>
        <Button variant="default" onClick={() => navigate('/org-picker')}>
          {t('settings.switchOrganization')}
        </Button>
        <Button color="red" variant="light" onClick={handleLogout}>
          {t('auth.signOut')}
        </Button>
      </Group>
    </Stack>
  );
}

function BoxGrid({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 16,
      }}
    >
      {children}
    </div>
  );
}
